import { useState, useEffect, useMemo, useRef } from 'react';

export interface PerformanceSettings {
  /** Whether the device is mobile (based on screen size and touch) */
  isMobile: boolean;
  /** Whether touch devices should use the compact mobile-style chrome */
  isCompactTouchLayout: boolean;
  /** Whether the device appears to be low-powered */
  isLowPerformance: boolean;
  /** Recommended throttle interval for time updates (ms) */
  timeUpdateThrottleMs: number;
  /** Recommended YouTube polling interval (ms) */
  youtubePollingMs: number;
  /** Whether to use smooth scroll animations */
  useSmoothScroll: boolean;
  /** Whether to enable expensive word highlighting */
  enableWordHighlighting: boolean;
  /** Recommended debounce time for search (ms) */
  searchDebounceMs: number;
  /** How often to poll for background jobs (ms) */
  jobPollingMs: number;
  /** Slow polling interval when no active jobs (ms) */
  slowJobPollingMs: number;
}

const MOBILE_BREAKPOINT = 768;
const MOBILE_TOUCH_POINTS = 1;
const COMPACT_TOUCH_MIN_DIMENSION = 600;
const COMPACT_TOUCH_MAX_HEIGHT = 720;

const isTouchCapableDevice = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }

  return (
    navigator.maxTouchPoints >= MOBILE_TOUCH_POINTS
    || window.matchMedia?.('(pointer: coarse)')?.matches
    || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );
};

const getViewportDimensions = () => {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }

  const viewport = window.visualViewport;
  return {
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
  };
};

/**
 * Detects device capabilities and returns appropriate performance settings.
 * This hook helps optimize the app for different devices, especially mobile.
 */
export function useDevicePerformance(): PerformanceSettings {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.innerWidth <= MOBILE_BREAKPOINT ||
      isTouchCapableDevice()
    );
  });
  const [isCompactTouchLayout, setIsCompactTouchLayout] = useState(() => {
    if (typeof window === 'undefined') return false;

    const { width, height } = getViewportDimensions();
    return isTouchCapableDevice() && (
      Math.min(width, height) <= COMPACT_TOUCH_MIN_DIMENSION
      || height <= COMPACT_TOUCH_MAX_HEIGHT
    );
  });

  // isLowPerformance is calculated once on mount and never changes
  // (hardware doesn't change during session)
  const isLowPerformanceRef = useRef(() => {
    if (typeof navigator === 'undefined') return false;
    // Check hardware concurrency (CPU cores)
    const cores = navigator.hardwareConcurrency || 4;
    // Check device memory (if available)
    const memory = (navigator as any).deviceMemory || 4;
    // Low performance if limited cores or memory
    return cores <= 2 || memory <= 2;
  });
  const isLowPerformance = isLowPerformanceRef.current();

  // Listen for window resize to update mobile status
  useEffect(() => {
    const handleResize = () => {
      const touchCapable = isTouchCapableDevice();
      const { width, height } = getViewportDimensions();
      const mobile = (
        window.innerWidth <= MOBILE_BREAKPOINT ||
        touchCapable
      );
      setIsMobile(mobile);
      setIsCompactTouchLayout(
        touchCapable && (
          Math.min(width, height) <= COMPACT_TOUCH_MIN_DIMENSION
          || height <= COMPACT_TOUCH_MAX_HEIGHT
        )
      );
    };

    // Also detect if user rotates device
    const handleOrientationChange = () => {
      // Small delay to let viewport settle
      setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate performance settings based on device characteristics
  const settings = useMemo<PerformanceSettings>(() => {
    if (isMobile || isLowPerformance) {
      return {
        isMobile,
        isCompactTouchLayout,
        isLowPerformance,
        // Slower updates on mobile - 500ms is still responsive enough
        timeUpdateThrottleMs: 500,
        // Less frequent YouTube polling
        youtubePollingMs: 500,
        // Instant scroll on mobile - smooth scroll is too CPU-intensive
        useSmoothScroll: false,
        // Disable word-level highlighting on mobile (use segment-level instead)
        enableWordHighlighting: false,
        // Longer search debounce
        searchDebounceMs: 500,
        // Slower job polling
        jobPollingMs: 5000,
        slowJobPollingMs: 30000,
      };
    }

    // Desktop / high-performance settings
    return {
      isMobile: false,
      isCompactTouchLayout,
      isLowPerformance: false,
      timeUpdateThrottleMs: 150,
      youtubePollingMs: 250,
      useSmoothScroll: true,
      enableWordHighlighting: true,
      searchDebounceMs: 200,
      jobPollingMs: 2000,
      slowJobPollingMs: 10000,
    };
  }, [isCompactTouchLayout, isLowPerformance, isMobile]);

  return settings;
}

/**
 * Simple hook that just returns whether we're on mobile
 */
export function useIsMobile(): boolean {
  const { isMobile } = useDevicePerformance();
  return isMobile;
}
